import { CATALOG_SETTINGS_LABEL } from './catalog'
import { ROLES } from './roles'

type RouteItem = {
  PATH: string
  LABEL: string
  TITLE?: string
  PARENT?: string
}

type MenuRoute = RouteItem & {
  ICON_NAME: string
  ROLES: number[]
}

export const MENU_ROUTES: Record<string, MenuRoute> = {
  HOME: {
    PATH: '/',
    LABEL: 'Dashboard',
    ICON_NAME: 'DashboardIcon',
    ROLES: [ROLES.ADMIN, ROLES.COMPANY_ADMIN],
  },
  COMPANIES: {
    PATH: '/companies',
    LABEL: 'Empresas',
    ICON_NAME: 'BusinessIcon',
    ROLES: [ROLES.ADMIN],
  },
  CONVERSATIONS: {
    PATH: '/conversations',
    LABEL: 'Atendimento',
    ICON_NAME: 'ConversationsIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  SCHEDULED_MESSAGES: {
    PATH: '/scheduled-messages',
    LABEL: 'Agendamentos',
    ICON_NAME: 'ScheduledMessagesIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  CLIENTS: {
    PATH: '/clients',
    LABEL: 'Clientes',
    ICON_NAME: 'ClientsIcon',
    ROLES: [ROLES.COMPANY_ADMIN],
  },
  OPPORTUNITIES: {
    PATH: '/opportunities',
    LABEL: 'Oportunidades',
    ICON_NAME: 'WorkOutlineIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  INDICATORS: {
    PATH: '/indicators',
    LABEL: 'Indicadores',
    ICON_NAME: 'IndicatorsIcon',
    ROLES: [ROLES.COMPANY_ADMIN],
  },
  ADMIN_INDICATORS: {
    PATH: '/admin-indicators',
    LABEL: 'Indicadores',
    ICON_NAME: 'IndicatorsIcon',
    ROLES: [ROLES.ADMIN],
  },
  ASSISTANT: {
    PATH: '/assistant',
    LABEL: 'Assistente',
    ICON_NAME: 'AssistantIcon',
    ROLES: [],
  },
  JOURNEYS: {
    PATH: '/journeys',
    LABEL: 'Jornadas',
    ICON_NAME: 'JourneysIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  TRANSMISSION_LIST: {
    PATH: '/transmission-lists',
    LABEL: 'Listas de transmissão',
    ICON_NAME: 'TransmissionListsIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  CAMPAIGNS: {
    PATH: '/campaigns',
    LABEL: 'Campanhas',
    ICON_NAME: 'CampaignsIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  INDICATION_PROGRAM: {
    PATH: '/indication-program',
    LABEL: 'Programa de indicação',
    ICON_NAME: 'IndicationProgramIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  TASKS: {
    PATH: '/tasks',
    LABEL: 'Tarefas',
    ICON_NAME: 'ChecklistIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  SERVICE_ORDERS: {
    PATH: '/service-orders',
    LABEL: 'Ordens de Serviço',
    ICON_NAME: 'ServiceOrdersIcon',
    ROLES: [ROLES.COMPANY_ADMIN, ROLES.ATTENDANT, ROLES.SPECIALIST],
  },
  HELP_CENTER: {
    PATH: '/help-center',
    LABEL: 'Central de Ajuda',
    ICON_NAME: 'HelpCenterIcon',
    ROLES: [
      ROLES.ADMIN,
      ROLES.COMPANY_ADMIN,
      ROLES.ATTENDANT,
      ROLES.SPECIALIST,
    ],
  },
}

export const CONVERSATION_BASE = '/conversations'
export const CONVERSATION_VIEW_BASE = '/conversations/view'
export const TRANSMISSION_LIST_VIEW_BASE = '/transmission-lists/view'

export const ROUTES: Record<string, RouteItem> = {
  ROOT: {
    PATH: '/',
    LABEL: 'Dashboard',
  },
  REGISTER: {
    PATH: '/cadastro',
    LABEL: 'Cadastro',
  },
  LOGIN: {
    PATH: '/login',
    LABEL: 'Login',
  },
  CREATE_PASSWORD: {
    PATH: '/new-password',
    LABEL: 'Nova senha',
  },
  REQUEST_RESET_PASSWORD: {
    PATH: '/request-reset-password',
    LABEL: 'Solicitar redefinição de senha',
  },
  RESET_PASSWORD: {
    PATH: '/reset-password',
    LABEL: 'Redefinir senha',
  },
  ASSISTANT: {
    PATH: MENU_ROUTES.ASSISTANT.PATH,
    LABEL: MENU_ROUTES.ASSISTANT.LABEL,
  },
  CONVERSATION_VIEW: {
    PATH: `${CONVERSATION_VIEW_BASE}/:id`,
    LABEL: 'Chat',
    TITLE: 'Atendimento',
    PARENT: MENU_ROUTES.CONVERSATIONS.PATH,
  },
  UPDATE_COMPANY: {
    PATH: '/companies/:id',
    LABEL: 'Editar empresa',
    PARENT: MENU_ROUTES.COMPANIES.PATH,
  },
  CREATE_COMPANY: {
    PATH: '/companies/new',
    LABEL: 'Nova empresa',
    PARENT: MENU_ROUTES.COMPANIES.PATH,
  },
  UPDATE_USER: {
    PATH: '/users/:id',
    LABEL: 'Editar usuário',
    PARENT: '/users',
  },
  CREATE_USER: {
    PATH: '/users/new',
    LABEL: 'Novo usuário',
    PARENT: '/users',
  },
  LIST_NICHES: {
    PATH: '/settings/niches',
    LABEL: 'Segmentos',
    PARENT: '/settings',
  },
  UPDATE_NICHE: {
    PATH: '/settings/niches/:id',
    LABEL: 'Editar segmento',
    PARENT: '/settings/niches',
  },
  CREATE_NICHE: {
    PATH: '/settings/niches/new',
    LABEL: 'Novo segmento',
    PARENT: '/settings/niches',
  },
  LIST_PLANS: {
    PATH: '/settings/plans',
    LABEL: 'Planos',
    PARENT: '/settings',
  },
  AUDIO_TRANSCRIPTION_USAGE: {
    PATH: '/settings/audio-transcription',
    LABEL: 'Transcrição de áudios',
    PARENT: '/settings',
  },
  UPDATE_PLAN: {
    PATH: '/settings/plans/:id',
    LABEL: 'Editar plano',
    PARENT: '/settings/plans',
  },
  CREATE_PLAN: {
    PATH: '/settings/plans/new',
    LABEL: 'Novo plano',
    PARENT: '/settings/plans',
  },
  LIST_TUTORIALS: {
    PATH: '/settings/tutorials',
    LABEL: 'Tutoriais',
    PARENT: '/settings',
  },
  UPDATE_TUTORIAL: {
    PATH: '/settings/tutorials/:id',
    LABEL: 'Editar tutorial',
    PARENT: '/settings/tutorials',
  },
  CREATE_TUTORIAL: {
    PATH: '/settings/tutorials/new',
    LABEL: 'Novo tutorial',
    PARENT: '/settings/tutorials',
  },
  LIST_HELP_RESOURCES: {
    PATH: '/settings/help-center',
    LABEL: 'Central de Ajuda',
    PARENT: '/settings',
  },
  UPDATE_HELP_RESOURCE: {
    PATH: '/settings/help-center/:id',
    LABEL: 'Editar material',
    PARENT: '/settings/help-center',
  },
  CREATE_HELP_RESOURCE: {
    PATH: '/settings/help-center/new',
    LABEL: 'Novo material',
    PARENT: '/settings/help-center',
  },
  LIST_NOTIFICATIONS: {
    PATH: '/settings/notifications',
    LABEL: 'Avisos',
    PARENT: '/settings',
  },
  SYSTEM_UPDATE_NOTICE: {
    PATH: '/settings/system-update',
    LABEL: 'Atualização do sistema',
    PARENT: '/settings',
  },
  UPDATE_NOTIFICATION: {
    PATH: '/settings/notifications/:id',
    LABEL: 'Editar aviso',
    PARENT: '/settings/notifications',
  },
  CREATE_NOTIFICATION: {
    PATH: '/settings/notifications/new',
    LABEL: 'Novo aviso',
    PARENT: '/settings/notifications',
  },
  LIST_DEPARTMENTS: {
    PATH: '/settings/departments',
    LABEL: 'Departamentos',
    PARENT: '/settings',
  },
  UPDATE_DEPARTMENT: {
    PATH: '/settings/departments/:id',
    LABEL: 'Editar departamento',
    PARENT: '/settings/departments',
  },
  CREATE_DEPARTMENT: {
    PATH: '/settings/departments/new',
    LABEL: 'Novo departamento',
    PARENT: '/settings/departments',
  },
  LIST_TAGS: {
    PATH: '/settings/tags',
    LABEL: 'Tags',
    PARENT: '/settings',
  },
  UPDATE_TAG: {
    PATH: '/settings/tags/:id',
    LABEL: 'Editar tag',
    PARENT: '/settings/tags',
  },
  CREATE_TAG: {
    PATH: '/settings/tags/new',
    LABEL: 'Nova tag',
    PARENT: '/settings/tags',
  },
  LIST_DEMANDS: {
    PATH: '/settings/demands',
    LABEL: 'Demandas',
    PARENT: '/settings',
  },
  VIEW_DEMAND: {
    PATH: '/settings/demands/view/:id',
    LABEL: 'Visualizar demanda',
    PARENT: '/settings/demands',
  },
  UPDATE_DEMAND: {
    PATH: '/settings/demands/:id',
    LABEL: 'Editar demanda',
    PARENT: '/settings/demands',
  },
  CREATE_DEMAND: {
    PATH: '/settings/demands/new',
    LABEL: 'Nova demanda',
    PARENT: '/settings/demands',
  },
  IMPORT_DEMAND: {
    PATH: '/settings/demands/import',
    LABEL: 'Importar demandas',
    PARENT: '/settings/demands',
  },
  LIST_CLIENT_ORIGINS: {
    PATH: '/settings/client-origins',
    LABEL: 'Origens de clientes',
    PARENT: '/settings',
  },
  UPDATE_CLIENT_ORIGIN: {
    PATH: '/settings/client-origins/:id',
    LABEL: 'Editar origem de cliente',
    PARENT: '/settings/client-origins',
  },
  CREATE_CLIENT_ORIGIN: {
    PATH: '/settings/client-origins/new',
    LABEL: 'Nova origem de cliente',
    PARENT: '/settings/client-origins',
  },
  LIST_CAC_EXPENSES: {
    PATH: '/settings/cac-expenses',
    LABEL: 'Despesas de CAC',
    PARENT: '/settings',
  },
  UPDATE_CAC_EXPENSE: {
    PATH: '/settings/cac-expenses/:id',
    LABEL: 'Editar despesa de CAC',
    PARENT: '/settings/cac-expenses',
  },
  CREATE_CAC_EXPENSE: {
    PATH: '/settings/cac-expenses/new',
    LABEL: 'Nova despesa de CAC',
    PARENT: '/settings/cac-expenses',
  },
  LIST_JOURNEY_TEMPLATES: {
    PATH: '/settings/journey-templates',
    LABEL: 'Jornadas padrão',
    PARENT: '/settings',
  },
  CREATE_JOURNEY_TEMPLATE: {
    PATH: '/settings/journey-templates/new',
    LABEL: 'Nova jornada padrão',
    PARENT: '/settings/journey-templates',
  },
  UPDATE_JOURNEY_TEMPLATE: {
    PATH: '/settings/journey-templates/:id',
    LABEL: 'Editar jornada padrão',
    PARENT: '/settings/journey-templates',
  },
  ANNIVERSARY_MESSAGES: {
    PATH: '/settings/anniversary-messages',
    LABEL: 'Mensagens de aniversário',
    PARENT: '/settings',
  },
  COMMEMORATIVE_MESSAGES: {
    PATH: '/settings/commemorative-messages',
    LABEL: 'Mensagens comemorativas',
    PARENT: '/settings',
  },
  CREATE_COMMEMORATIVE_MESSAGE: {
    PATH: '/settings/commemorative-messages/new',
    LABEL: 'Nova mensagem comemorativa',
    PARENT: '/settings/commemorative-messages',
  },
  UPDATE_COMMEMORATIVE_MESSAGE: {
    PATH: '/settings/commemorative-messages/:id',
    LABEL: 'Editar mensagem comemorativa',
    PARENT: '/settings/commemorative-messages',
  },
  UPDATE_ANNIVERSARY_MESSAGE: {
    PATH: '/settings/anniversary-messages/:key',
    LABEL: 'Editar mensagem de aniversário',
    PARENT: '/settings/anniversary-messages',
  },
  DEPARTMENT_ROUTING: {
    PATH: '/settings/department-routing',
    LABEL: 'Bot de atendimento inicial',
    PARENT: '/settings',
  },
  CONVERSATION_FINISH: {
    PATH: '/settings/conversation-finish',
    LABEL: 'Finalização de atendimento',
    PARENT: '/settings',
  },
  WAITING_CONVERSATION_MONITOR: {
    PATH: '/settings/waiting-conversation-monitor',
    LABEL: 'Monitor de atendimentos aguardando',
    PARENT: '/settings',
  },
  QUICK_MESSAGES: {
    PATH: '/settings/quick-messages',
    LABEL: 'Mensagens rápidas',
    PARENT: '/settings',
  },
  WHATSAPP_TEMPLATES: {
    PATH: '/settings/whatsapp-templates',
    LABEL: 'Templates WhatsApp',
    PARENT: '/settings',
  },
  WHATSAPP_COSTS: { PATH: '/settings/whatsapp-costs', LABEL: 'Tarifas da API oficial', PARENT: '/settings' },
  WHATSAPP_ROUTING: {
    PATH: '/settings/whatsapp-routing',
    LABEL: 'Uso da API oficial',
    PARENT: '/settings',
  },
  NEW_WHATSAPP_TEMPLATE: {
    PATH: '/settings/whatsapp-templates/new',
    LABEL: 'Novo template WhatsApp',
    PARENT: '/settings/whatsapp-templates',
  },
  UPDATE_WHATSAPP_TEMPLATE: {
    PATH: '/settings/whatsapp-templates/:id',
    LABEL: 'Editar template WhatsApp',
    PARENT: '/settings/whatsapp-templates',
  },
  ABSENCE_MESSAGES: {
    PATH: '/settings/absence-messages',
    LABEL: 'Mensagens de ausência',
    PARENT: '/settings',
  },
  INTEGRATIONS: {
    PATH: '/settings/integrations',
    LABEL: 'Integrações',
    PARENT: '/settings',
  },
  GCLICK_INTEGRATION: {
    PATH: '/settings/integrations/gclick',
    LABEL: 'G-Click',
    PARENT: '/settings/integrations',
  },
  OMIE_INTEGRATION: {
    PATH: '/settings/integrations/omie',
    LABEL: 'Omie',
    PARENT: '/settings/integrations',
  },
  UPDATE_SETTING: {
    PATH: '/settings/:key',
    LABEL: 'Editar configuração',
    PARENT: '/settings',
  },
  UPDATE_CLIENT: {
    PATH: '/clients/:id',
    LABEL: 'Editar cliente',
    PARENT: MENU_ROUTES.CLIENTS.PATH,
  },
  CREATE_CLIENT: {
    PATH: '/clients/new',
    LABEL: 'Novo cliente',
    PARENT: MENU_ROUTES.CLIENTS.PATH,
  },
  IMPORT_CLIENT: {
    PATH: '/clients/import',
    LABEL: 'Importar clientes',
    PARENT: MENU_ROUTES.CLIENTS.PATH,
  },
  VIEW_JOURNEY: {
    PATH: '/journeys/view/:id',
    LABEL: 'Visualizar jornada',
    PARENT: MENU_ROUTES.JOURNEYS.PATH,
  },
  LIST_JOURNEY_TEMPLATES_TO_IMPORT: {
    PATH: '/journeys/view/templates',
    LABEL: 'Jornadas padrão disponíveis',
    PARENT: MENU_ROUTES.JOURNEYS.PATH,
  },
  VIEW_JOURNEY_TEMPLATE_TO_IMPORT: {
    PATH: '/journeys/view/templates/:id',
    LABEL: 'Visualizar jornada padrão',
    PARENT: '/journeys/view/templates',
  },
  VIEW_CLIENT_JOURNEY: {
    PATH: '/journeys/view/client/:clientId',
    LABEL: 'Jornada do cliente',
    PARENT: MENU_ROUTES.JOURNEYS.PATH,
  },
  UPDATE_JOURNEY: {
    PATH: '/journeys/:id',
    LABEL: 'Editar jornada',
    PARENT: MENU_ROUTES.JOURNEYS.PATH,
  },
  CREATE_JOURNEY: {
    PATH: '/journeys/new',
    LABEL: 'Nova jornada',
    PARENT: MENU_ROUTES.JOURNEYS.PATH,
  },
  UPDATE_SERVICE_ORDER: {
    PATH: '/service-orders/:id',
    LABEL: 'Editar ordem de serviço',
    PARENT: MENU_ROUTES.SERVICE_ORDERS.PATH,
  },
  CREATE_SERVICE_ORDER: {
    PATH: '/service-orders/new',
    LABEL: 'Nova ordem de serviço',
    PARENT: MENU_ROUTES.SERVICE_ORDERS.PATH,
  },
  VIEW_SERVICE_ORDER: {
    PATH: '/service-orders/view/:id',
    LABEL: 'Visualizar ordem de serviço',
    PARENT: MENU_ROUTES.SERVICE_ORDERS.PATH,
  },
  VIEW_TRANSMISSION_LIST: {
    PATH: `${TRANSMISSION_LIST_VIEW_BASE}/:id`,
    LABEL: 'Visualizar lista',
    PARENT: MENU_ROUTES.TRANSMISSION_LIST.PATH,
  },
  UPDATE_TRANSMISSION_LIST: {
    PATH: '/transmission-lists/:id',
    LABEL: 'Editar lista',
    PARENT: MENU_ROUTES.TRANSMISSION_LIST.PATH,
  },
  CREATE_TRANSMISSION_LIST: {
    PATH: '/transmission-lists/new',
    LABEL: 'Nova lista',
    PARENT: MENU_ROUTES.TRANSMISSION_LIST.PATH,
  },
  PRODUCTS_SERVICES: {
    PATH: '/settings/products-services',
    LABEL: CATALOG_SETTINGS_LABEL,
    PARENT: '/settings',
  },
  SETTINGS: {
    PATH: '/settings',
    LABEL: 'Configurações',
  },
  BILLING_UPDATE: {
    PATH: '/billing/update',
    LABEL: 'Atualizar assinatura',
  },
  USERS: {
    PATH: '/users',
    LABEL: 'Usuários',
  },
  ...MENU_ROUTES,
}

export const ROUTE_LIST: RouteItem[] = Object.values(ROUTES)
